import React, { useState, useCallback, useEffect } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";

interface PreviewImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  className?: string;
  imageClassName?: string;
  allowZoom?: boolean;
  zoomIconClassName?: string;
  containerSelector?: string;
}

export const PreviewImage = React.forwardRef<
  HTMLImageElement,
  PreviewImageProps
>(
  (
    {
      src,
      alt,
      className,
      imageClassName,
      allowZoom = true,
      zoomIconClassName,
      containerSelector = "#psa-chat-framework",
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isError, setIsError] = useState(false);

    // 获取容器元素用于 Portal
    const getContainer = useCallback(() => {
      const container = document.querySelector(containerSelector);
      return (container as Element) || document.body;
    }, [containerSelector]);

    const handleImageClick = () => {
      if (allowZoom && !isError && src) {
        setIsOpen(true);
      }
    };

    const handleImageError = () => {
      setIsError(true);
    };

    useEffect(() => {
      if (!src) {
        setIsError(true);
        return;
      } else {
        setIsError(false);
      }
    }, [src]);

    return (
      <>
        {/* 原始图片 */}
        <div
          className={cn(
            "relative w-full h-full inline-flex items-center justify-center overflow-hidden",
            allowZoom && "cursor-pointer",
            className
          )}
          onClick={handleImageClick}
        >
          {!isError ? (
            <img
              ref={ref}
              src={src}
              alt={alt}
              onError={handleImageError}
              className={cn(
                "transition-opacity duration-200 object-contain",
                allowZoom && "hover:opacity-90",
                imageClassName
              )}
              {...props}
            />
          ) : (
            <img
              src={`https://res.cloudinary.com/castlery/image/upload/w_1000/v1760521309/static/default_image.png`}
              alt={alt}
              className={cn(
                "transition-opacity duration-200 object-contain",
                imageClassName
              )}
            />
          )}
        </div>

        {/* 大图弹窗 */}
        {allowZoom && (
          <DialogPrimitive.Root open={isOpen} onOpenChange={setIsOpen}>
            <DialogPrimitive.Portal container={getContainer()}>
              {/* 遮罩层 */}
              <DialogPrimitive.Overlay
                className={cn(
                  "absolute inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
                )}
              />

              {/* 大图内容 */}
              <DialogPrimitive.Content
                className={cn(
                  "absolute top-[50%] z-50 translate-y-[-50%] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
                )}
              >
                <div className="relative flex items-center justify-center">
                  {/* 大图 */}
                  <img
                    src={src}
                    alt={alt}
                    className="w-full max-h-full object-contain shadow-2xl"
                    // onClick={(e) => e.stopPropagation()}
                    onClick={() => setIsOpen(false)}
                  />
                </div>
              </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
          </DialogPrimitive.Root>
        )}
      </>
    );
  }
);
